import React from "react";
import Icon from "../icons";
import { ElementType } from "@/constants/tagular/main";
import useElementEvents from "@/hooks/eventing/useElementEvents";

function extractGroupedRowsFromElement(
  element: React.ReactNode,
  elementClicked: (args: { outboundUrl: string }) => void,
): React.ReactNode[] {
  const table = React.Children.toArray(
    (element as React.ReactElement<any>).props.children,
  ).find((child: any) => React.isValidElement(child) && child.type === "table");
  if (!table || !React.isValidElement(table)) return [];

  const thead = React.Children.toArray(
    (table as React.ReactElement<any>).props.children,
  ).find((child: any) => React.isValidElement(child) && child.type === "thead");
  let headers: string[] = [];
  if (thead && React.isValidElement(thead)) {
    const headerRow = React.Children.toArray(
      (thead as React.ReactElement<any>).props.children,
    ).find((child: any) => React.isValidElement(child) && child.type === "tr");
    if (headerRow && React.isValidElement(headerRow)) {
      headers = React.Children.toArray(
        (headerRow as React.ReactElement<any>).props.children,
      )
        .filter((cell: any) => React.isValidElement(cell) && cell.type === "th")
        .map((cell: any) =>
          React.isValidElement(cell)
            ? typeof (cell as React.ReactElement<any>).props.children ===
              "string"
              ? (cell as React.ReactElement<any>).props.children
              : ""
            : "",
        );
    }
  }

  const tbody = React.Children.toArray(
    (table as React.ReactElement<any>).props.children,
  ).find((child: any) => React.isValidElement(child) && child.type === "tbody");
  if (!tbody || !React.isValidElement(tbody)) return [];

  const rows = React.Children.toArray(
    (tbody as React.ReactElement<any>).props.children,
  ).filter((child: any) => React.isValidElement(child) && child.type === "tr");

  const groups: Record<string, React.ReactElement[]> = {};
  let currentGroup = "";

  rows.forEach((row: any) => {
    const cells = React.Children.toArray(row.props.children);
    const firstTh = cells.find(
      (cell: any) =>
        React.isValidElement(cell) &&
        cell.type === "th" &&
        (cell as React.ReactElement<any>).props.scope === "row",
    );
    if (firstTh) {
      currentGroup = (firstTh as React.ReactElement<any>).props.children;
      if (!groups[currentGroup]) groups[currentGroup] = [];
    }
    if (currentGroup) {
      groups[currentGroup].push(row);
    }
  });

  return Object.entries(groups).map(([groupName, groupRows], idx, arr) => {
    const isFirst = idx === 0;
    const isLast = idx === arr.length - 1;
    const roundedClass =
      isFirst && isLast
        ? "rounded-[12px]"
        : isFirst
          ? "rounded-t-[12px]"
          : isLast
            ? "rounded-b-[12px]"
            : "";

    const firstRow = groupRows[0];
    let blockBgClass = "bg-white";
    if (React.isValidElement(firstRow)) {
      blockBgClass =
        (firstRow as React.ReactElement<any>).props.className || blockBgClass;
    }

    const columns: Record<string, React.ReactNode[]> = {};
    headers.forEach((header, colIdx) => {
      columns[header] = [];
      groupRows.forEach((row: any) => {
        const cells = React.Children.toArray(row.props.children);
        let dataCells = cells.filter(
          (cell: any) => React.isValidElement(cell) && cell.type === "td",
        );
        if (colIdx === 0) {
          const thCell = cells.find(
            (cell: any) => React.isValidElement(cell) && cell.type === "th",
          );
          columns[header].push(
            thCell ? (thCell as React.ReactElement<any>).props.children : "",
          );
        } else {
          columns[header].push(
            dataCells[colIdx - 1]
              ? (dataCells[colIdx - 1] as React.ReactElement<any>).props
                  .children
              : "",
          );
        }
      });
    });

    return (
      <div
        key={idx}
        className={`${blockBgClass} flex flex-col p-6 responsive-table ${roundedClass}`}
      >
        {typeof groupName === "string" && (
          <div className="font-bold mb-2 text-lg">{groupName}</div>
        )}
        {headers.map((header, colIdx) =>
          colIdx === 0 ? null : (
            <div className="mb-4" key={header}>
              <div className="font-bold mb-2">{header}</div>
              <div className="flex flex-row flex-wrap gap-2">
                {columns[header].every((v) => v === "yes" || v === "no") ? (
                  columns[header].map((value, index) =>
                    value === "yes" ? (
                      <Icon
                        key={index}
                        type="circle-check-fill"
                        svgProps={{ width: "24", height: "24" }}
                        pathProps={{ fill: "#1F453D" }}
                      />
                    ) : (
                      <Icon
                        key={index}
                        type="circle-close-fill"
                        svgProps={{ width: "24", height: "24" }}
                        pathProps={{ fill: "#D74000" }}
                      />
                    ),
                  )
                ) : columns[header].every((v) => v === columns[header][0]) ? (
                  <div>{columns[header][0]}</div>
                ) : (
                  columns[header].map((value, i) => {
                    if (React.isValidElement(value) && value.type === "a") {
                      const anchorElement = value as React.ReactElement<
                        React.AnchorHTMLAttributes<HTMLAnchorElement>
                      >;
                      return React.cloneElement(anchorElement, {
                        onClick: (e: React.MouseEvent<HTMLAnchorElement>) => {
                          elementClicked({
                            outboundUrl: anchorElement.props.href ?? "",
                          });
                          if (
                            typeof anchorElement.props.onClick === "function"
                          ) {
                            anchorElement.props.onClick(e);
                          }
                        },
                        key: i,
                      });
                    }
                    return <div key={i}>{value}</div>;
                  })
                )}
              </div>
            </div>
          ),
        )}
      </div>
    );
  });
}

export const MobileBlocks = ({ children }: { children: React.ReactNode }) => {
  const { elementClicked } = useElementEvents({
    webElement: {
      elementType: ElementType.Link,
      location: "responsive-table",
      name: "responsive-table-link",
      text: "responsive-table-link",
    },
  });
  const mobileBlocks = children
    ? extractGroupedRowsFromElement(children, elementClicked)
    : null;

  return (
    <div className="block md:hidden rounded-[12px] border-putty-400 shadow mb-2">
      {mobileBlocks}
    </div>
  );
};
